import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Popover,
  Select,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { rawApi } from '../../api/client';
import { errorCopy } from '../../api/errors';
import { detailKey, listKey } from '../../api/queryKeys';
import { EntitySelect } from '../../components/EntityField';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { enumOptionsFor } from '../../lib/enums';
import { formatMoney, formatQty, formatRateBp, type NumberInputValue, typedNumber } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { PriceEditor } from '../catalog/RingsUpAt';
import { marginTone, RECIPES_PATH, type RecipeRow, useRecipes, useStockItems, useUnitOptions } from './stock';

/** What a component is made of. The same two words as the recipe's target, meaning something else. */
const COMPONENT_KINDS = [
  { value: 'inventoryItem', label: 'Something you buy and count' },
  { value: 'recipe', label: 'Something made in house' },
];

/** Which way a line of an extra's recipe points. Only ever anything but `consumes` on an extra. */
type ComponentEffect = 'consumes' | 'returns';

interface CostedComponent {
  componentType: 'inventoryItem' | 'recipe';
  componentId: string;
  qty: number;
  unit: string;
  effect?: ComponentEffect;
  /**
   * The same amount in the unit the shelf is counted in, trim loss included — 45 mL of a
   * case-counted rum reads 0.005 case. This is the number the cost beside it was worked out from,
   * and showing it is what makes the plate cost checkable by hand instead of taken on trust.
   */
  stockQty: number;
  stockUnit: string;
  yieldLossBp: number;
  costBaseMinor: number;
  /** Nothing has ever been paid for this one, so its zero is an absence rather than a price. */
  costMissing: boolean;
}

/**
 * What the saved version costs, what the dish it makes sells for, and what is left.
 *
 * The margin arrives worked out. It has to: the menu is written in lempiras and the books are kept
 * in dollars, so the subtraction crosses a currency and belongs on the side that has the recorded
 * exchange rate and the money arithmetic — never in a screen.
 */
interface SavedRecipeCost {
  costBaseMinor: number;
  /** How many ingredients have no price on file. Above zero, the cost is a floor and not a cost. */
  uncostedComponents?: number;
  components: CostedComponent[];
  /**
   * What each extra someone can add to this dish does to the figure above. A plate cost is not one
   * number: a cheeseburger is $2.05, or $3.25 with bacon, or $1.80 without the cheese.
   */
  options?: {
    modifierOptionId: string;
    nameEn: string;
    costBaseMinor: number | null;
    uncostedComponents: number;
  }[];
  sellsForMinor?: number | null;
  sellsForCurrency?: string | null;
  sellsForBaseMinor?: number | null;
  marginBp?: number | null;
}

interface ComponentRow {
  key: string;
  componentType: 'inventoryItem' | 'recipe';
  componentId: string;
  /** `returns` is a "No cheese" line: it comes off what the dish would otherwise have used. */
  effect: ComponentEffect;
  qty: NumberInputValue;
  /** The unit the amount is written in. Empty means the unit the thing is counted in. */
  unit?: string;
  /** What that amount comes to on the shelf, in the saved version. */
  savedStockQty?: number;
  savedStockUnit?: string;
  /** Trim loss as the percentage a person says out loud; stored as basis points. */
  trimLossPct: NumberInputValue;
  /** What this line costs in the version currently saved, where there is one. */
  savedCostBaseMinor?: number;
  /** …and whether that figure is a price or the absence of one. */
  savedCostMissing?: boolean;
}

let keySeq = 0;
const blankRow = (): ComponentRow => ({
  key: `component-${++keySeq}`,
  componentType: 'inventoryItem',
  componentId: '',
  effect: 'consumes',
  qty: '',
  trimLossPct: '',
});

/**
 * What a dish is made of, and what that costs.
 *
 * The recipes tab could only ever list names: there was no way to add a recipe, no way to change
 * one, and no way to see either the ingredients or the cost — which is the entire reason a bar
 * keeps recipes at all. Everything needed was already computed by the API and thrown away.
 *
 * Ingredients and cost live in ONE place on purpose. "What is in the burger" and "why does the
 * burger cost L 4.16" are the same question asked twice, and splitting them across a read screen
 * and an edit screen means changing a quantity without ever seeing what it did to the plate.
 */
export function RecipeForm({
  recipe,
  locationId,
  currency,
  onClose,
}: {
  /** null when adding a recipe. */
  recipe: RecipeRow | null;
  locationId?: string;
  currency: string;
  onClose: () => void;
}) {
  const saved = useQuery({
    queryKey: detailKey(RECIPES_PATH, recipe?.id ?? 'new'),
    queryFn: () => rawApi<SavedRecipeCost>('GET', `${RECIPES_PATH}/${recipe?.id}/cost`),
    enabled: Boolean(recipe),
  });

  if (recipe && saved.isLoading) {
    return (
      <Modal opened onClose={onClose} title={recipe.name} size="xl">
        <Group gap="xs" py="xl" justify="center">
          <Loader size="sm" />
          <Text c="dimmed" size="sm">
            Loading…
          </Text>
        </Group>
      </Modal>
    );
  }

  /*
   * A recipe the API cannot cost — a component in a unit with no route to the shelf's, a sub-recipe
   * that contains itself — answers with a refusal rather than a figure, and the ingredients do not
   * come back with it. Falling through would open the form on an empty ingredient list under a cost
   * of nothing, and saving that would replace a real recipe with a blank one.
   */
  if (recipe && saved.isError) {
    return (
      <Modal opened onClose={onClose} title={recipe.name} size="xl">
        <Alert color="orange" variant="light" title="This one cannot be worked out">
          <Text size="sm">{errorCopy(saved.error, 'open this recipe').message}</Text>
          <Text size="sm" mt="xs">
            It is usually the amount for one shelf unit missing on an ingredient. Put that in and this will
            open.
          </Text>
        </Alert>
      </Modal>
    );
  }

  const rows: ComponentRow[] = (saved.data?.components ?? []).map((c) => ({
    key: `component-${++keySeq}`,
    componentType: c.componentType,
    componentId: c.componentId,
    effect: c.effect ?? 'consumes',
    qty: c.qty,
    unit: c.unit,
    trimLossPct: c.yieldLossBp ? c.yieldLossBp / 100 : '',
    savedCostBaseMinor: c.costBaseMinor,
    savedCostMissing: c.costMissing,
    savedStockQty: c.stockQty,
    savedStockUnit: c.stockUnit,
  }));

  return (
    <RecipeFormFields
      recipe={recipe}
      locationId={locationId}
      currency={currency}
      savedCostBaseMinor={saved.data?.costBaseMinor ?? 0}
      saved={saved.data}
      initialRows={rows.length > 0 ? rows : [blankRow()]}
      onClose={onClose}
    />
  );
}

/**
 * What the dish is worth making at.
 *
 * The recipe screen used to show the cost and nothing else, which is half a fact: "costs L 4.16 to
 * make" is not a decision until it sits next to what a guest pays. Answering that meant noting the
 * cost, going to the pricing screen, checking a price, and subtracting in your head — per dish.
 *
 * A prep batch is deliberately silent about price: the house grind is made, not sold, and putting a
 * margin on a thing nobody can buy would be an invented number.
 */
function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.06em' }}>
        {label}
      </Text>
      <div style={{ marginTop: 2 }}>{children}</div>
    </div>
  );
}

function WorthMaking({
  saved,
  currency,
  recipe,
}: {
  saved: SavedRecipeCost;
  currency: string;
  recipe: RecipeRow;
}) {
  const queryClient = useQueryClient();
  const [pricing, setPricing] = useState(false);
  // An extra that only leaves things out costs a negative amount. True, and unreadable as a minus
  // sign under a heading that says "Costs to make".
  const takesOff = saved.costBaseMinor < 0;
  const cost = formatMoney(Math.abs(saved.costBaseMinor), currency);
  // An extra is sold — "Add bacon, $2.00" — so it is asked what it sells for and what is left, on
  // exactly the same terms as a dish. A prep batch is made rather than sold and stays silent.
  const sellable = recipe.targetType === 'variant' || recipe.targetType === 'modifierOption';
  const margin = typeof saved.marginBp === 'number' ? saved.marginBp : null;
  /*
   * Whether "at most — it can only come down" is even true.
   *
   * It is, for a dish: every ingredient with no price on file is a cost the plate will eventually
   * carry. On an extra that TAKES something off, a missing price works the other way — the credit
   * is understated, so the margin is a floor and not a ceiling. Saying "it can only come down"
   * there would be exactly as wrong as saying nothing.
   */
  const onlyAdds = !(saved.components ?? []).some((c) => c.effect === 'returns');
  /*
   * Ingredients nothing has ever been paid for. They cost zero in the roll-up, which is arithmetic
   * and not a fact — and the two figures either side of it, the plate cost and what is left after
   * ingredients, both inherit that zero and both look finished. So the cost is qualified rather
   * than stated, and the margin says which direction it is wrong in: it can only come DOWN when
   * the missing prices arrive, never up.
   */
  const short = saved.uncostedComponents ?? 0;

  // What was just changed is the price of the dish, so what has to be re-read is this dish's
  // margin and its row on the list behind — otherwise the figure sitting beside the price the
  // manager just corrected goes on describing the old one.
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: detailKey(RECIPES_PATH, recipe.id) });
    void queryClient.invalidateQueries({ queryKey: listKey(RECIPES_PATH) });
    setPricing(false);
  };

  return (
    <Group gap="xl" wrap="wrap">
      <Figure label={takesOff ? 'Takes off the plate' : 'Costs to make'}>
        <Text size="sm" fw={500}>
          {short > 0 ? `at least ${cost}` : cost}
        </Text>
      </Figure>

      {sellable && (
        <>
          <Figure label="Sells for">
            {/*
              The price is editable from here because that is the decision this screen exists to
              provoke. A margin the owner does not like is fixed by changing what the dish sells
              for, and until now that meant leaving, finding the size on the pricing screen, and
              coming back to see whether it helped. Same editor as the menu screens use, so there
              is one set of rules about which list a new price lands on.
            */}
            <Popover
              opened={pricing}
              onChange={setPricing}
              // Upwards: these figures sit near the bottom of the dialog, and a dropdown opening
              // downwards from them is cut off by the edge of it.
              position="top-start"
              withArrow
              shadow="md"
              trapFocus
              withinPortal
            >
              <Popover.Target>
                <UnstyledButton
                  onClick={() => setPricing((o) => !o)}
                  title={
                    typeof saved.sellsForMinor === 'number'
                      ? 'Change what this sells for'
                      : 'Put a price on this'
                  }
                  style={{ borderBottom: '1px dashed var(--mantine-color-dimmed)' }}
                >
                  {typeof saved.sellsForMinor === 'number' ? (
                    <Text size="sm" fw={500}>
                      {formatMoney(saved.sellsForMinor, saved.sellsForCurrency ?? currency)}
                      {/* Both moneys when they differ: the cost is in one and the menu in the
                          other, and the margin is only readable if you can see them meet. */}
                      {saved.sellsForCurrency &&
                      saved.sellsForCurrency !== currency &&
                      saved.sellsForBaseMinor != null
                        ? ` · ${formatMoney(saved.sellsForBaseMinor, currency)}`
                        : ''}
                    </Text>
                  ) : (
                    <Badge color="orange" variant="light" style={{ cursor: 'pointer' }}>
                      Set a price
                    </Badge>
                  )}
                </UnstyledButton>
              </Popover.Target>
              <Popover.Dropdown>
                {/* The kind of thing a price goes on, which is now two of them: an extra is
                    priced against `modifierOption`, and sending `variant` would write the price
                    of a choice onto a size whose id it never was. */}
                <PriceEditor
                  targetType={recipe.targetType === 'modifierOption' ? 'modifierOption' : 'variant'}
                  targetId={recipe.targetId}
                  name={recipe.name}
                  onDone={refresh}
                />
              </Popover.Dropdown>
            </Popover>
          </Figure>

          <Figure label="Left after ingredients">
            <Text size="sm" fw={600} c={marginTone(margin)}>
              {margin === null ? 'Nothing prices it yet' : formatRateBp(margin)}
            </Text>
            {margin !== null && short > 0 && (
              <Text size="xs" c="orange.7">
                {onlyAdds ? 'at most — it can only come down' : 'not final — some of it has no price yet'}
              </Text>
            )}
          </Figure>
        </>
      )}
      {short > 0 && (
        <Text size="xs" c="orange.7" w="100%">
          {short === 1
            ? 'One ingredient has never been bought, so nothing knows what it costs and it is counted here as free.'
            : `${short} ingredients have never been bought, so nothing knows what they cost and they are counted here as free.`}{' '}
          Record a delivery for {short === 1 ? 'it' : 'them'} and these figures fill in.
        </Text>
      )}
      <WithTheExtras plate={saved.costBaseMinor} options={saved.options ?? []} currency={currency} />
    </Group>
  );
}

/**
 * What each extra does to the plate cost, and what the plate comes to with it on.
 *
 * The figure above is the cost of the dish as written, and for a dish anybody actually orders that
 * is only part of the answer: a cheeseburger is $2.05, or $3.25 with bacon, or $1.80 without the
 * cheese. Until an extra could carry a recipe there was nothing to say so and the plate cost was
 * silently the cost of the burger nobody orders.
 *
 * Both numbers, because the two questions are different ones. "What does bacon add" is the pricing
 * decision — is $2.00 for it worth charging — and "what does the plate come to with it" is the one
 * the owner is actually holding when he looks at the menu.
 *
 * Renders nothing when no extra on this dish carries a recipe, which is every dish before somebody
 * writes one.
 */
function WithTheExtras({
  plate,
  options,
  currency,
}: {
  plate: number;
  options: NonNullable<SavedRecipeCost['options']>;
  currency: string;
}) {
  if (options.length === 0) return null;
  return (
    <div style={{ width: '100%' }}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.06em' }} mb={4}>
        With the extras
      </Text>
      <Stack gap={2}>
        {options.map((option) => (
          <Group key={option.modifierOptionId} gap="xs" wrap="nowrap">
            <Text size="sm" style={{ flex: 1 }}>
              {option.nameEn}
            </Text>
            {option.costBaseMinor === null ? (
              // Uncostable, not free. The same two opposite statements the plate cost itself
              // refuses to conflate.
              <Text size="sm" c="orange.7">
                cannot be worked out
              </Text>
            ) : (
              <>
                <Text size="sm" c={option.costBaseMinor < 0 ? 'teal.7' : 'dimmed'}>
                  {option.costBaseMinor < 0 ? '−' : '+'}
                  {formatMoney(Math.abs(option.costBaseMinor), currency)}
                </Text>
                <Text size="sm" fw={500}>
                  = {formatMoney(plate + option.costBaseMinor, currency)}
                </Text>
              </>
            )}
          </Group>
        ))}
      </Stack>
    </div>
  );
}

function RecipeFormFields({
  recipe,
  locationId,
  currency,
  savedCostBaseMinor,
  saved,
  initialRows,
  onClose,
}: {
  recipe: RecipeRow | null;
  locationId?: string;
  currency: string;
  savedCostBaseMinor: number;
  saved?: SavedRecipeCost;
  initialRows: ComponentRow[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const stock = useStockItems();
  const unitOptions = useUnitOptions();
  const recipes = useRecipes();
  const items = useEntityIndex('inventoryItem');
  const variants = useEntityIndex('variant');

  const [name, setName] = useState(recipe?.name ?? '');
  const [targetType, setTargetType] = useState<RecipeRow['targetType']>(recipe?.targetType ?? 'variant');
  const [targetId, setTargetId] = useState(recipe?.targetId ?? '');
  const [yieldQty, setYieldQty] = useState<NumberInputValue>(recipe?.yieldQty ?? '');
  const [rows, setRows] = useState<ComponentRow[]>(initialRows);
  const [error, setError] = useState<string | null>(null);

  const isBatch = targetType === 'inventoryItem';
  /**
   * Only an extra may take something back off (RCP-03). A dish uses ingredients; it does not hand
   * them back, and the API refuses one that claims to — so the control is not offered where the
   * answer can only be one thing.
   */
  const isExtra = targetType === 'modifierOption';
  /**
   * A batch is measured in the unit the thing it produces is counted in — anything else and the
   * cost of one unit of that batch is divided by a number in a different unit, which is how a prep
   * item ends up costing a thousand times what it should.
   */
  const batchUnit = isBatch ? stock.unitOf(targetId) : '';

  /**
   * The unit a component is used in.
   *
   * A sub-recipe is consumed by the unit it yields and there is nothing to choose. A stock item is
   * offered every unit a recorded conversion can reach from the one it is counted in, defaulting to
   * that one — so a recipe can say 45 mL of a rum bought by the case, which it could not before,
   * and a person who does not want to think about it gets the answer that is always right.
   */
  const shelfUnitOf = (row: ComponentRow) =>
    row.componentType === 'inventoryItem'
      ? stock.unitOf(row.componentId)
      : (recipes.byId.get(row.componentId)?.yieldUnit ?? '');

  const unitOf = (row: ComponentRow) => row.unit || shelfUnitOf(row);

  const unitChoicesFor = (row: ComponentRow) =>
    row.componentType === 'inventoryItem' ? unitOptions(row.componentId, shelfUnitOf(row)) : [];

  const nameOf = (row: ComponentRow) =>
    row.componentType === 'inventoryItem'
      ? items.label(row.componentId)
      : (recipes.byId.get(row.componentId)?.name ?? '');

  const setRow = (index: number, patch: Partial<ComponentRow>) =>
    setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const filled = rows.filter((r) => r.componentId);

  const save = useMutation({
    mutationFn: () =>
      rawApi('POST', RECIPES_PATH, {
        locationId,
        targetType,
        targetId,
        name,
        yieldQty: isBatch ? (typedNumber(yieldQty) ?? null) : null,
        yieldUnit: isBatch && batchUnit ? batchUnit : null,
        components: filled.map((r) => ({
          componentType: r.componentType,
          componentId: r.componentId,
          qty: Number(r.qty),
          unit: unitOf(r),
          yieldLossBp: Math.round((typedNumber(r.trimLossPct) ?? 0) * 100),
          // Never anything but `consumes` off an extra: the control below is only shown there, and
          // sending a stale `returns` after switching the target is what the API refuses.
          effect: isExtra ? r.effect : 'consumes',
        })),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey(RECIPES_PATH) });
      notifyDone(
        recipe ? `Saved a new version of ${name}` : `Added ${name}`,
        recipe
          ? 'The old version is kept, so what past sales cost to make has not changed.'
          : 'It will be costed from what you are currently paying for its ingredients.',
      );
      onClose();
    },
    onError: (err) => notifyError(err, 'save this recipe'),
  });

  const submit = () => {
    if (!name.trim()) return setError('Give the recipe a name');
    if (!targetId) return setError(isBatch ? 'Say what this batch produces' : 'Say what this makes');
    if (filled.length === 0) return setError('Add at least one ingredient');
    const noQty = filled.find((r) => (typedNumber(r.qty) ?? 0) <= 0);
    if (noQty) return setError(`Say how much ${nameOf(noQty) || 'of each ingredient'} goes in`);
    const noUnit = filled.find((r) => !unitOf(r));
    if (noUnit) {
      return setError(
        noUnit.componentType === 'inventoryItem'
          ? `${nameOf(noUnit)} has no unit set — fill in "Counted in" under Stock items first`
          : `${nameOf(noUnit)} does not say what a batch of it makes — set that on that recipe first`,
      );
    }
    if (isBatch && !batchUnit) {
      return setError(
        'The item this batch produces has no unit set — fill in "Counted in" under Stock items',
      );
    }
    setError(null);
    save.mutate();
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={recipe ? recipe.name : 'Add a recipe'}
      size="xl"
      // Half-completed forms are lost to a stray click otherwise.
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <TextInput
          label="Name"
          required
          placeholder="Smokehouse Burger"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          disabled={save.isPending}
        />

        {recipe ? (
          // Changing what a recipe makes would not edit it — it would leave the old dish with its
          // old recipe still in force and quietly start a second one somewhere else.
          <div>
            <Text size="xs" c="dimmed">
              Makes
            </Text>
            <Text size="sm" fw={500}>
              {recipe.targetType === 'variant'
                ? variants.label(recipe.targetId)
                : items.label(recipe.targetId)}
            </Text>
          </div>
        ) : (
          <>
            <Select
              label="What it makes"
              required
              // Wording comes from the enum registry, not from here. Deleting the old "Makes"
              // column left that group with no reader, and a second copy of the same two labels
              // sitting in this file is how the picker and a future screen end up calling the
              // same thing by two different names.
              data={enumOptionsFor('recipeTargetType', ['variant', 'inventoryItem', 'modifierOption'])}
              value={targetType}
              onChange={(v) => {
                setTargetType((v as RecipeRow['targetType']) ?? 'variant');
                setTargetId('');
                // Switching away from an extra leaves "takes this back off" lines behind that no
                // longer mean anything and that the API refuses. Reset rather than sent and
                // rejected: the person is looking at the form and can see them change.
                setRows((current) => current.map((r) => ({ ...r, effect: 'consumes' })));
              }}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
              disabled={save.isPending}
            />
            <EntitySelect
              entity={isExtra ? 'modifierOption' : isBatch ? 'inventoryItem' : 'variant'}
              label={
                isExtra ? 'Which extra' : isBatch ? 'Which prepped item it produces' : 'Which dish or drink'
              }
              required
              value={targetId || null}
              onChange={(v) => setTargetId(v ?? '')}
            />
            {isExtra && (
              <Text size="xs" c="dimmed">
                Writing this is what makes an extra move stock and count towards the plate. Add bacon takes
                the bacon out of the fridge; no cheese cancels the slice the dish would have used, and never
                puts one back that was not taken.
              </Text>
            )}
          </>
        )}

        {isBatch && (
          <NumberInput
            label="One batch makes"
            min={0}
            suffix={batchUnit ? ` ${batchUnit}` : undefined}
            description={
              batchUnit
                ? `Counted in ${batchUnit}, the same as everywhere else for that item. Leave blank if the recipe makes exactly one.`
                : 'Pick what it produces first and this will show what that is counted in'
            }
            value={yieldQty}
            onChange={setYieldQty}
            disabled={save.isPending}
          />
        )}

        <div>
          <Text size="sm" fw={500}>
            What goes in it
          </Text>
          <Text size="xs" c="dimmed" mb={6}>
            Each ingredient is measured in the unit it is already counted in. Trim loss is what never reaches
            the plate — peel, bone, the ends of the tenderloin.
          </Text>
          <Stack gap="xs">
            {rows.map((row, index) => {
              const unit = unitOf(row);
              const choices = unitChoicesFor(row);
              return (
                <Paper key={row.key} withBorder p="xs" radius="sm">
                  <Group gap="xs" align="flex-end" wrap="wrap">
                    <Select
                      label="Kind"
                      data={COMPONENT_KINDS}
                      value={row.componentType}
                      onChange={(v) =>
                        setRow(index, {
                          componentType: (v as 'inventoryItem' | 'recipe') ?? 'inventoryItem',
                          componentId: '',
                          unit: undefined,
                          savedCostBaseMinor: undefined,
                          savedStockQty: undefined,
                        })
                      }
                      allowDeselect={false}
                      comboboxProps={{ withinPortal: true }}
                      style={{ width: 210 }}
                    />
                    <div style={{ minWidth: 220, flex: 1 }}>
                      <EntitySelect
                        entity={row.componentType === 'inventoryItem' ? 'inventoryItem' : 'recipe'}
                        label="Ingredient"
                        value={row.componentId || null}
                        // The unit belongs to the thing, so changing the thing drops the unit
                        // rather than carrying millilitres over onto something counted in pounds.
                        onChange={(v) => setRow(index, { componentId: v ?? '', unit: undefined })}
                      />
                    </div>
                    {/* Only on an extra. A dish uses ingredients and cannot give them back, so
                        asking the question there is offering an answer the API refuses. */}
                    {isExtra && (
                      <Select
                        label="Which way"
                        data={enumOptionsFor('recipeComponentEffect', ['consumes', 'returns'])}
                        value={row.effect}
                        onChange={(v) => setRow(index, { effect: (v as ComponentEffect) ?? 'consumes' })}
                        allowDeselect={false}
                        comboboxProps={{ withinPortal: true }}
                        style={{ width: 180 }}
                      />
                    )}
                    <NumberInput
                      label="How much"
                      min={0}
                      style={{ width: choices.length > 1 ? 110 : 150 }}
                      suffix={choices.length > 1 || !unit ? undefined : ` ${unit}`}
                      value={row.qty}
                      onChange={(v) => setRow(index, { qty: v })}
                    />
                    {/* Only when there is a choice to make. One option is not a question, and a
                        dropdown with one entry in it is a thing to click past on every line. */}
                    {choices.length > 1 && (
                      <Select
                        label="In"
                        data={choices}
                        value={unit}
                        onChange={(v) => setRow(index, { unit: v ?? undefined })}
                        allowDeselect={false}
                        comboboxProps={{ withinPortal: true }}
                        style={{ width: 100 }}
                      />
                    )}
                    <NumberInput
                      label="Trim loss"
                      min={0}
                      max={99.99}
                      suffix="%"
                      style={{ width: 120 }}
                      value={row.trimLossPct}
                      onChange={(v) => setRow(index, { trimLossPct: v })}
                    />
                    {row.savedCostBaseMinor !== undefined && (
                      <Stack gap={0} pb={6}>
                        {/* "$ 0.00" against a can of Coke is not what a can of Coke costs — it is
                            what the system knows about what a can of Coke costs, which is nothing,
                            because none has ever been delivered. Written out as words so nobody
                            adds it up. */}
                        {row.savedCostMissing ? (
                          <Text size="sm" c="orange.7">
                            no price yet
                          </Text>
                        ) : row.savedCostBaseMinor < 0 ? (
                          // A "takes this back off" line contributes a negative amount, which is
                          // the truth and reads as a typo in a money column. Said as what it does.
                          <Text size="sm" c="dimmed">
                            −{formatMoney(-row.savedCostBaseMinor, currency)} off the plate
                          </Text>
                        ) : (
                          <Text size="sm" c="dimmed">
                            {formatMoney(row.savedCostBaseMinor, currency)}
                          </Text>
                        )}
                        {/* What that amount actually is on the shelf. Shown only when it is not
                            the same words as what was typed — otherwise it is the line repeated
                            back. This is the step that used to happen in somebody's head, and it
                            is the difference between a plate cost you can check and one you take
                            on trust. */}
                        {row.savedStockQty !== undefined &&
                          row.savedStockUnit &&
                          (row.savedStockUnit !== unit || row.savedStockQty !== Number(row.qty)) && (
                            <Text size="xs" c="dimmed">
                              {row.savedStockQty < 0 ? 'left on the shelf: ' : 'off the shelf: '}
                              {formatQty(Math.abs(row.savedStockQty), row.savedStockUnit)}
                            </Text>
                          )}
                      </Stack>
                    )}
                    <Button
                      size="compact-sm"
                      variant="subtle"
                      color="red"
                      onClick={() => setRows(rows.filter((_, i) => i !== index))}
                    >
                      Remove
                    </Button>
                  </Group>
                </Paper>
              );
            })}
            <Button
              size="compact-sm"
              variant="light"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setRows([...rows, blankRow()])}
            >
              Add an ingredient
            </Button>
          </Stack>
        </div>

        {recipe && (
          <Paper withBorder p="sm" radius="sm">
            <WorthMaking
              saved={saved ?? { costBaseMinor: savedCostBaseMinor, components: [] }}
              currency={currency}
              recipe={recipe}
            />
            {/* The figures above describe the version currently SAVED. Editing an ingredient
                changes what the dish will cost, and the recosting happens on save — saying so is
                better than showing a margin that quietly stops matching what is on screen. */}
            <Text size="xs" c="dimmed" mt="xs">
              As the saved version stands, at what you are currently paying. Amounts beside each ingredient
              are its share of the cost; change one and these are worked out again when you save.
            </Text>
          </Paper>
        )}

        {recipe && (
          <Alert color="blue" variant="light" title="Saving keeps the old version">
            Changing a recipe does not rewrite history: the version you are replacing is kept, so what past
            sales cost to make stays as it was on the day they were sold.
          </Alert>
        )}

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={submit}>
            {recipe ? 'Save changes' : 'Add recipe'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
